import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { QStack, QText } from '@captivateiq/quotient';

import assistantI18n from '@captivateiq/assistant/i18n';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import { AgentIcon } from '@captivateiq/assistant/assets';
import { getAgentConfig } from '@captivateiq/assistant/config/agentConfig';
import type { AgentNames } from '@captivateiq/assistant/types';
import type { AgentDefinition } from '@captivateiq/assistant/hooks/useAgentDiscovery';

type AgentCardProps = {
  agentName?: AgentNames;
  agent?: AgentDefinition;
};

export const AgentCard = ({ agentName, agent }: AgentCardProps) => {
  const { openIntroFor } = useAIShellContext();
  const { t } = useTranslation('assistant', { i18n: assistantI18n });

  // Support both legacy agentName prop and new agent prop
  const resolvedAgentName = agentName || (agent?.name as AgentNames);

  // For new agent discovery, prefer kit name for consistency
  const iconKey = agent?.kit_name || resolvedAgentName;
  const translationKey = agent?.kit_name || resolvedAgentName;

  const config = agent
    ? {
        name: agent.title,
        short_description:
          agent.description.length > 150 ? `${agent.description.substring(0, 147)}...` : agent.description,
      }
    : getAgentConfig(translationKey, t);

  // Icon key for the AgentIcon component
  const agentKitName = iconKey;

  const handleAgentSelection = useCallback(() => {
    if (resolvedAgentName) {
      // Pass the kit name for consistent translation lookups, but fall back to agent name for legacy support
      const identifier = agent?.kit_name || resolvedAgentName;
      openIntroFor(identifier);
    }
  }, [openIntroFor, resolvedAgentName, agent?.kit_name]);

  return (
    <QStack
      direction="row"
      spacing="10px"
      sx={{
        backgroundColor: 'white',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: '8px',
        cursor: 'pointer',
        justifyContent: 'stretch',
        padding: '12px',
        '&:hover': {
          backgroundColor: 'grey.100',
        },
      }}
      onClick={handleAgentSelection}
    >
      <AgentIcon agentKitName={agentKitName} height={40} width={40} />
      <QStack direction="column" spacing="4px" sx={{ flex: 1, minWidth: 0 }}>
        <QText
          sx={{
            fontSize: '14px',
            fontWeight: 800,
            lineHeight: '22px',
            color: 'text.onSurface.background.highContrast',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            hyphens: 'auto',
          }}
        >
          {config.name}
        </QText>
        <QText
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '20px',
            color: 'text.onSurface.background.mediumContrast',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            hyphens: 'auto',
          }}
        >
          {config.short_description}
        </QText>
      </QStack>
    </QStack>
  );
};
